import type { Role } from '@koderek/shared';
import { AppShell, Avatar, Badge, Burger, Button, Container, Divider, Group, NavLink, ScrollArea, Stack, Text, Tooltip } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import type { ComponentType } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router';
import {
  IconAdjustments,
  IconCalendar,
  IconCalendarCheck,
  IconChartBar,
  IconClipboardCheck,
  IconFileCheck,
  IconFileImport,
  IconHome,
  IconLogin,
  IconLogout,
  IconMail,
  IconMailFooter,
  IconSettings,
  IconMessage,
  IconPartners,
  IconQrcode,
  IconSchool,
  IconSearch,
  IconShieldCheck,
  IconSpeakerphone,
  IconTag,
  IconUser,
  IconUsers,
} from '../components/icons';
import { useLogout, useMe } from '../features/auth/session';
import { FeedbackButton } from '../features/feedback/FeedbackButton';
import { useFeedbackSummary } from '../features/feedback/summary';
import { useLanding } from '../features/site/landing';
import { recordPoweredByClick } from '../features/site/poweredBy';
import { t } from '../texts/pl';
import classes from './Layout.module.css';

type NavItem = {
  to: string;
  label: string;
  icon: ComponentType<{ size?: number }>;
  /** Inne początki adresów, przy których pozycja też jest aktywna (np. `/odprawa/:token` → „Odprawa”). */
  also?: string[];
  exact?: boolean;
  /** Licznik przy pozycji (np. nowe opinie). */
  badge?: number;
};
type NavSection = { label?: string; items: NavItem[] };

/** Pozycje panelu bocznego według ról (widok; serwer sprawdza uprawnienia sam – NFR-S4). */
function navSections(roles: Role[] | null, landing: boolean, newFeedback = 0): NavSection[] {
  const main: NavItem[] = landing
    ? [
        // Strona startowa (PRD §10 pkt 37) pod `/`, lista edycji pod `/edycje`.
        { to: '/', label: t.nav.start, icon: IconHome, exact: true },
        { to: '/edycje', label: t.nav.editions, icon: IconCalendar },
      ]
    : [
        // Bez strony startowej jedna pozycja: przy jednej edycji `/` otwiera od razu jej stronę, przy kilku – listę.
        { to: '/', label: t.nav.editions, icon: IconCalendar, exact: true, also: ['/edycje'] },
      ];
  if (!roles) return [{ items: main }];
  const staff = roles.includes('staff') || roles.includes('admin');
  // Obsługa (bez roli admina) widzi tylko swoją sekcję – bez menu rodzica (ekrany rodziny działają dalej pod adresami,
  // „Moje konto” jest pod kółkiem z inicjałem w nagłówku). Admin i prowadzący mają menu rodzica – mogą zapisywać swoją rodzinę.
  const familyMenu = roles.includes('admin') || !roles.includes('staff');
  const sections: NavSection[] = !familyMenu
    ? []
    : [
        {
          items: [
            ...main,
            { to: '/zajecia-rodziny', label: t.nav.familyActivities, icon: IconCalendarCheck },
            { to: '/kod-qr', label: t.nav.qr, icon: IconQrcode },
            { to: '/rodzina', label: t.nav.family, icon: IconUsers },
            { to: '/konto', label: t.nav.account, icon: IconUser },
          ],
        },
      ];
  if (staff) {
    const checkIn: NavItem = { to: '/obsluga/odprawa', label: t.checkIn.nav, icon: IconQrcode, also: ['/odprawa/'] };
    sections.push({
      label: t.nav.staff,
      // Obsługa bez roli admina ma w menu tylko punkt rejestracji; obecność, rodziny i statystyki działają dalej pod adresami.
      // Statystyki (US-801) w menu tylko dla admina – wybór edycji albo całego dnia na stronie (PRD §10 pkt 57).
      items: familyMenu
        ? [
            checkIn,
            { to: '/obsluga/zajecia', label: t.attendance.nav, icon: IconClipboardCheck },
            { to: '/obsluga/konta', label: t.nav.families, icon: IconSearch },
            { to: '/statystyki', label: t.nav.stats, icon: IconChartBar },
          ]
        : [checkIn],
    });
  } else if (roles.includes('speaker')) {
    sections.push({ label: t.nav.speaker, items: [{ to: '/obsluga/zajecia', label: t.attendance.speakerNav, icon: IconSchool }] });
  }
  if (roles.includes('admin')) {
    sections.push({
      label: t.nav.admin,
      items: [
        { to: '/admin/edycje', label: t.nav.adminEditions, icon: IconAdjustments },
        { to: '/admin/import', label: t.program.import.nav, icon: IconFileImport },
        { to: '/admin/typy-zajec', label: t.program.typesTitle, icon: IconTag },
        { to: '/admin/konta', label: t.adminAccounts.nav, icon: IconShieldCheck },
        { to: '/admin/zgody', label: t.consentsAdmin.nav, icon: IconFileCheck },
        { to: '/admin/kampanie', label: t.campaigns.nav, icon: IconSpeakerphone },
        { to: '/admin/opinie', label: t.feedback.nav, icon: IconMessage, badge: newFeedback },
        { to: '/admin/poczta', label: t.mailLog.nav, icon: IconMail },
        { to: '/admin/stopka-maili', label: t.mailFooter.nav, icon: IconMailFooter },
        { to: '/admin/strona-startowa', label: t.landing.nav, icon: IconHome },
        { to: '/admin/partnerzy', label: t.partners.nav, icon: IconPartners },
        { to: '/admin/ustawienia', label: t.settings.nav, icon: IconSettings },
      ],
    });
  }
  return sections;
}

/** `exact` dotyczy tylko `to` (np. `/` nie może łapać każdego adresu); `also` zawsze działa jak początek adresu. */
function isActive(item: NavItem, pathname: string): boolean {
  if (item.exact && pathname === item.to) return true;
  const prefixes = item.exact ? (item.also ?? []) : [item.to, ...(item.also ?? [])];
  return prefixes.some((p) => pathname === p || pathname.startsWith(p.endsWith('/') ? p : `${p}/`));
}

/** Menu w panelu po lewej (na telefonie wysuwane przyciskiem), logo w lewym górnym rogu. */
export function Layout() {
  const [opened, { toggle, close }] = useDisclosure(false);
  const me = useMe();
  return (
    <AppShell header={{ height: 72 }} navbar={{ width: 260, breakpoint: 'sm', collapsed: { mobile: !opened } }} padding="md">
      <AppShell.Header className={classes.header}>
        <Group h="100%" px="md" justify="space-between" wrap="nowrap">
          <Group gap="sm" wrap="nowrap">
            <Burger opened={opened} onClick={toggle} hiddenFrom="sm" size="sm" aria-label={t.nav.menu} />
            <Link to="/" className={classes.logoLink} aria-label={t.nav.logoHome}>
              <img src="/logo.png" alt={t.app.name} className={classes.logo} />
            </Link>
            <Text visibleFrom="lg" ml="md" className={classes.tagline}>
              <span className={classes.taglineMark}>{t.app.tagline}</span>
            </Text>
          </Group>
          <Group gap={6} wrap="nowrap">
            {me.isPending ? null : me.data ? (
              <Tooltip label={`${me.data.firstName} ${me.data.lastName}`}>
                <Avatar component={Link} to="/konto" color="brand" radius="xl" size={36} aria-label={t.nav.account}>
                  {me.data.firstName.slice(0, 1).toUpperCase()}
                </Avatar>
              </Tooltip>
            ) : (
              // Na telefonie oba przyciski też – mniejsze, „Zaloguj” bez „się”, żeby zmieściły się obok logo.
              <>
                <Button component={Link} to="/logowanie" variant="default" size="compact-sm" px={8} hiddenFrom="xs">
                  {t.nav.loginShort}
                </Button>
                <Button component={Link} to="/logowanie" variant="subtle" size="sm" visibleFrom="xs">
                  {t.nav.login}
                </Button>
                <Button component={Link} to="/rejestracja" size="compact-sm" px={8} hiddenFrom="xs">
                  {t.nav.register}
                </Button>
                <Button component={Link} to="/rejestracja" size="sm" visibleFrom="xs">
                  {t.nav.register}
                </Button>
              </>
            )}
          </Group>
        </Group>
      </AppShell.Header>

      <AppShell.Navbar className={classes.navbar}>
        <SideNav onNavigate={close} />
      </AppShell.Navbar>

      <AppShell.Main className={classes.main}>
        <Container size="lg" px={0} className={classes.content}>
          <Outlet />
        </Container>
        <footer className={classes.footer} aria-label={t.footer.label}>
          <div className={classes.footerInner}>
            {/* Logo na jasnym tle, tak jak na stronie fundacji (białe litery z poświatą – decyzja właściciela). */}
            <img src="/hybrydowa-szkola.png" alt={t.footer.logoAlt} className={classes.footerLogo} />
            <div className={classes.footerText}>
              <p>
                <strong>{t.footer.foundation}</strong> | {t.footer.address}
              </p>
              <p>{t.footer.registry}</p>
              <p>
                <strong>{t.footer.bank}</strong>
              </p>
              <p className={classes.copyright}>{t.footer.copyright(new Date().getFullYear())}</p>
            </div>
          </div>
          <p className={classes.poweredBy}>
            {t.app.poweredBy}{' '}
            <a href="https://codifive.pl" target="_blank" rel="noopener noreferrer" onClick={recordPoweredByClick}>
              CodiFive
            </a>
          </p>
        </footer>
        {/* Opinie (PRD §10 pkt 53): na każdym ekranie, tylko dla zalogowanych. */}
        {me.data ? <FeedbackButton /> : null}
      </AppShell.Main>
    </AppShell>
  );
}

function SideNav({ onNavigate }: { onNavigate: () => void }) {
  const me = useMe();
  const logout = useLogout();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const landing = useLanding().data?.available ?? false;
  const isAdmin = me.data?.roles.includes('admin') ?? false;
  const newFeedback = useFeedbackSummary(isAdmin).data?.new ?? 0;
  const sections = navSections(me.data ? me.data.roles : null, landing, newFeedback);

  return (
    <>
      <AppShell.Section grow component={ScrollArea} p="sm">
        <nav aria-label={t.nav.menu}>
          <Stack gap="md">
            {sections.map((section, i) => (
              <Stack gap={2} key={section.label ?? i}>
                {section.label ? (
                  <Text size="xs" fw={700} tt="uppercase" c="dimmed" px="sm" pb={4} className={classes.sectionLabel}>
                    {section.label}
                  </Text>
                ) : null}
                {section.items.map((item) => {
                  const active = isActive(item, pathname);
                  return (
                    <NavLink
                      key={item.to}
                      component={Link}
                      to={item.to}
                      label={item.label}
                      leftSection={<item.icon size={19} />}
                      rightSection={
                        item.badge ? (
                          <Badge size="sm" color="red" circle={item.badge < 10} aria-label={t.feedback.newCount(item.badge)}>
                            {item.badge}
                          </Badge>
                        ) : undefined
                      }
                      active={active}
                      aria-current={active ? 'page' : undefined}
                      variant="filled"
                      className={classes.navLink}
                      onClick={onNavigate}
                    />
                  );
                })}
              </Stack>
            ))}
          </Stack>
        </nav>
      </AppShell.Section>
      {me.data ? (
        <AppShell.Section p="sm">
          <Divider mb="sm" />
          <Text size="sm" fw={600} px="sm" truncate>
            {me.data.firstName} {me.data.lastName}
          </Text>
          <Text size="xs" c="dimmed" px="sm" mb="xs" truncate>
            {me.data.email}
          </Text>
          <NavLink
            component="button"
            label={t.nav.logout}
            leftSection={<IconLogout size={19} />}
            className={classes.navLink}
            onClick={() => {
              onNavigate();
              logout.mutate(undefined, { onSettled: () => void navigate('/') });
            }}
          />
        </AppShell.Section>
      ) : me.isPending ? null : (
        // Gość: logowanie i rejestracja także w menu (na telefonie nagłówek jest ciasny).
        <AppShell.Section p="sm">
          <Divider mb="sm" />
          <NavLink
            component={Link}
            to="/logowanie"
            label={t.nav.login}
            leftSection={<IconLogin size={19} />}
            className={classes.navLink}
            onClick={onNavigate}
          />
          <NavLink
            component={Link}
            to="/rejestracja"
            label={t.nav.register}
            leftSection={<IconUser size={19} />}
            className={classes.navLink}
            onClick={onNavigate}
          />
        </AppShell.Section>
      )}
    </>
  );
}
